import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import { PamGrid } from './PamGrid';
import { useDeltaDetection, createVersionColumn } from './useDeltaDetection';
import DeltaHistoryLegend from '../components/DeltaHistoryLegend';
import { formatDateTimeForDisplay } from '../utils/dateUtils';

// Stable references for default prop values to avoid unnecessary re-renders
const EMPTY_FIELD_NAMES = {};
const EMPTY_GRID_PROPS = {};

/**
 * HistoryGrid - Self-fetching composite history grid with delta detection.
 *
 * Eliminates 60-80 lines of duplicated boilerplate per history component.
 * Handles data fetching, sorting, delta detection, version column,
 * MODIFIED_BY/MODIFIED_DATE columns, loading/error/empty states, and
 * optional quick filtering.
 *
 * @param {Object} props
 * @param {Function}  props.fetchHistory           - REQUIRED: async fn returning { data: [...] }
 * @param {Array}     props.columns                - REQUIRED: AG Grid column defs (business fields only)
 * @param {Array}     [props.fetchParams=[]]        - Re-fetch dependency array
 * @param {Function}  [props.transformData]         - Optional: transform response.data before rendering
 * @param {Object}    [props.fieldNames={}]         - Friendly field names for delta tooltips
 * @param {boolean}   [props.showVersionColumn=true] - Show pinned version column
 * @param {boolean}   [props.showLegend=true]        - Show delta highlighting legend
 * @param {string}    [props.emptyMessage]           - Message when no data
 * @param {string}    [props.height='100%']          - Grid height
 * @param {Function}  [props.renderToolbar]          - Custom toolbar renderer
 * @param {Object}    [props.defaultColDef]          - User default column overrides
 * @param {Object}    [props.gridProps={}]           - Additional PamGrid props
 */
const HistoryGrid = ({
  fetchHistory,
  columns,
  fetchParams = [],
  transformData,
  fieldNames = EMPTY_FIELD_NAMES,
  showVersionColumn = true,
  showLegend = true,
  emptyMessage = 'No history data available',
  height = '100%',
  renderToolbar,
  defaultColDef: userDefaultColDef,
  gridProps = EMPTY_GRID_PROPS,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState('');

  // ---------------------------------------------------------------------------
  // Data fetching - re-fetch when fetchParams change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchHistory();

        if (cancelled) return;

        // Normalise: ensure we have an array, filter out nulls
        let data = response?.data;
        if (!Array.isArray(data)) {
          data = data != null ? [data] : [];
        }
        data = data.filter(Boolean);

        // Optional caller transform
        if (transformData) {
          data = transformData(data);
        }

        setRowData(data);
      } catch (err) {
        if (cancelled) return;
        console.error('HistoryGrid: failed to fetch history', err);
        setError('Failed to load history. Please try again.');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, fetchParams);

  // ---------------------------------------------------------------------------
  // Delta detection
  // ---------------------------------------------------------------------------
  const { sortedData, changedFields, getCellStyle, getCellTooltip } =
    useDeltaDetection(rowData);

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------
  const columnDefs = useMemo(() => {
    const cols = [];

    // 1. Prepend version column (if enabled)
    if (showVersionColumn) {
      cols.push(createVersionColumn({ sortedData, changedFields }));
    }

    // 2. Map user columns, auto-injecting delta styling unless caller overrides
    columns.forEach((col) => {
      const enriched = { ...col };

      // Auto-inject cellStyle if caller did not provide one
      if (!col.cellStyle) {
        enriched.cellStyle = getCellStyle;
      }

      // Auto-inject tooltipValueGetter if caller did not provide one
      if (!col.tooltipValueGetter) {
        enriched.tooltipValueGetter = (p) => getCellTooltip(p, fieldNames);
      }

      cols.push(enriched);
    });

    // 3. Append MODIFIED_BY column (no delta styling)
    cols.push({
      headerName: 'Modified By',
      field: 'MODIFIED_BY',
      minWidth: 120,
    });

    // 4. Append MODIFIED_DATE column (no delta styling)
    cols.push({
      headerName: 'Modified Date',
      field: 'MODIFIED_DATE',
      minWidth: 180,
      valueFormatter: (params) => formatDateTimeForDisplay(params.value),
    });

    return cols;
  }, [
    columns,
    sortedData,
    changedFields,
    getCellStyle,
    getCellTooltip,
    fieldNames,
    showVersionColumn,
  ]);

  // ---------------------------------------------------------------------------
  // Default column definition
  // ---------------------------------------------------------------------------
  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      filter: true,
      resizable: true,
      tooltipShowDelay: 0,
      flex: 1,
      minWidth: 120,
      ...userDefaultColDef,
    }),
    [userDefaultColDef]
  );

  // ---------------------------------------------------------------------------
  // Render: loading state
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading history...</Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: error state
  // ---------------------------------------------------------------------------
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: empty state
  // ---------------------------------------------------------------------------
  if (sortedData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
        }}
      >
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Determine whether PamGrid or external toolbar handles the QuickFilter
  // ---------------------------------------------------------------------------
  const hasCustomToolbar = typeof renderToolbar === 'function';

  // Destructure gridProps so we can merge containerSx/gridContainerSx
  const {
    containerSx: userContainerSx,
    gridContainerSx: userGridContainerSx,
    ...restGridProps
  } = gridProps;

  // ---------------------------------------------------------------------------
  // Render: data present
  // ---------------------------------------------------------------------------
  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column' }}>
      {/* Delta legend */}
      {showLegend && <DeltaHistoryLegend />}

      {/* Custom toolbar OR fallback to PamGrid's built-in QuickFilter */}
      {hasCustomToolbar &&
        renderToolbar({
          quickFilterText,
          onQuickFilterChange: setQuickFilterText,
        })}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <PamGrid
          rowData={sortedData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          height="100%"
          showToolbar={false}
          showFooter={false}
          showQuickFilter={!hasCustomToolbar}
          quickFilterText={quickFilterText}
          onQuickFilterChange={setQuickFilterText}
          getRowId={(row) => row.MODIFIED_DATE}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          {...restGridProps}
          containerSx={{
            flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
            ...userContainerSx,
          }}
          gridContainerSx={{
            flex: 1, minHeight: 0,
            ...userGridContainerSx,
          }}
        />
      </Box>
    </Box>
  );
};

HistoryGrid.displayName = 'HistoryGrid';

export default HistoryGrid;
